import { Settings } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { IntuiSlider } from '@/components/intueri/IntuiSlider'

import {
  LINE_WIDTH_MAX,
  LINE_WIDTH_MIN,
  LINE_WIDTH_STEP,
  type DisplaySettings,
} from './displaySettings'

/** The gear and its popover; see `displaySettings.ts` for what each control
 *  means and why the defaults are what they are. Presentational: state lives
 *  with the page so the canvas and the settings read one source. */
interface Props {
  settings: DisplaySettings
  onChange: (next: DisplaySettings) => void
}

const DisplaySettingsPopover = ({ settings, onChange }: Props) => {
  const { t } = useTranslation()
  const set = <K extends keyof DisplaySettings>(key: K, value: DisplaySettings[K]) =>
    onChange({ ...settings, [key]: value })

  const switches: Array<{ key: 'entityLabels' | 'edgeLabels' | 'orphans'; label: string }> = [
    { key: 'entityLabels', label: t('knowledge_graph:toolbar.entity_label', { defaultValue: 'Entity Label' }) },
    { key: 'edgeLabels', label: t('knowledge_graph:toolbar.edge_label', { defaultValue: 'Edge Label' }) },
    { key: 'orphans', label: t('knowledge_graph:toolbar.orphans', { defaultValue: 'Orphans' }) },
  ]
  const thicknessLabel = t('knowledge_graph:toolbar.line_thickness', { defaultValue: 'Line Thickness' })

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          // Matches the segmented control's height so the two sit level.
          className="h-9 w-9 rounded-md border-neutral-200 bg-background shadow-none dark:border-neutral-700"
          aria-label={String(t('knowledge_graph:toolbar.settings', { defaultValue: 'Display settings' }))}
        >
          <Settings className="size-4" strokeWidth={1.75} />
        </Button>
      </PopoverTrigger>
      {/* The design's Display popover at its true size (the Figma export is
          2x): 276px wide with 16px padding, 14px labels on a 28px pitch with
          teal switches, the thickness value as a bare number, an 8px slider
          track with a 16px black thumb. Its top sits 4px ABOVE the toolbar's
          bottom border — the card overlaps the line, where the Filter menu
          hangs from it. */}
      <PopoverContent align="end" sideOffset={9} className="w-[276px] rounded-xl px-4 py-4">
        <div className="flex flex-col gap-2">
          <span className="mb-1 text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {t('knowledge_graph:toolbar.display', { defaultValue: 'Display' })}
          </span>
          {switches.map(({ key, label }) => (
            <label
              key={key}
              className="flex items-center justify-between gap-3 text-sm font-medium text-foreground cursor-pointer select-none"
            >
              {label}
              <Switch
                aria-label={label}
                checked={settings[key]}
                onCheckedChange={(v) => set(key, v)}
                className="data-[state=checked]:bg-[#34728d] data-[state=unchecked]:bg-neutral-200 [&>span]:shadow-none"
              />
            </label>
          ))}
          <div className="flex flex-col gap-1 pt-1">
            <div className="flex items-center justify-between text-sm font-medium text-foreground">
              <span>{thicknessLabel}</span>
              <span className="tabular-nums" data-testid="kg-line-thickness-value">
                {settings.lineWidth.toFixed(1)}
              </span>
            </div>
            <IntuiSlider
              data-testid="kg-line-thickness"
              // The design's slider: an 8px pill track with a hairline grey outline,
              // light grey after the thumb and black up to it, and an 18px black
              // round thumb. The outline is an inset shadow rather than a border so
              // the black range paints over it instead of sitting inside it.
              className="[&>span:first-child]:h-2 [&>span:first-child]:bg-[#efefef] [&>span:first-child]:shadow-[inset_0_0_0_1px_#b3b3b3] [&>span:first-child>span]:bg-neutral-900 [&_[role=slider]]:size-4 [&_[role=slider]]:border-0 [&_[role=slider]]:bg-neutral-900 [&_[role=slider]]:shadow-none dark:[&>span:first-child]:bg-neutral-700 dark:[&>span:first-child]:shadow-[inset_0_0_0_1px_#525252] dark:[&>span:first-child>span]:bg-neutral-100 dark:[&_[role=slider]]:bg-neutral-100"
              min={LINE_WIDTH_MIN}
              max={LINE_WIDTH_MAX}
              step={LINE_WIDTH_STEP}
              value={[settings.lineWidth]}
              onValueChange={([v]) => set('lineWidth', v)}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DisplaySettingsPopover
